import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { type CamelCasedProperties } from 'type-fest'
import { useStore } from 'zustand'

import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'
import { appConfig, isPrivateEdition } from 'utils/app-config'

export type GremlinClientFlags = CamelCasedProperties<
  typeof DEFAULT_FEATURE_FLAG_VALUES
>
export type GremlinClientFlagsPartial = Partial<GremlinClientFlags>

/**
 * This list will provide sane default values for feature flags in the event that LD has an outage
 * and the LD SDK can't retrieve the feature flags from LD's servers
 */
export const DEFAULT_FEATURE_FLAG_VALUES = {
  'reliability-intelligence-analysis-chat': false,
  // INTENDED FOR LONGER-TERM USE
  'max-node-nesting': 5, // not a boolean value - 5 is the initial default
  // ACTIVELY USED FEATURE FLAGS BELOW - DO NOT REMOVE
  'aws-scenario-fi-integration': false,
  'create-service-from-scenario': true,
  'foresight-ad-on-login': false,
  'override-container-selection': false,
  'tpm-service-descriptions': false,
  'service-coverage-enabled': false,
  'service-coverage-suppress-window-hours': 48, // matches backend DEFAULT_SUPPRESS_WINDOW_HOURS
  'service-coverage-expire-window-hours': 168, // matches backend DEFAULT_EXPIRE_WINDOW_HOURS
  // NEED MORE INFO ON THESE FLAGS
  'cancel-subscription': false
}

function useUserContext() {
  const { currentTeamId, currentTeam, companyId, session } = useAuth()

  const userContext = useMemo(
    () => ({
      key: session.identifier,
      email: session.identifier,
      custom: {
        companyId: companyId,
        companyName: session.company_name,
        teamName: currentTeam.name,
        teamId: currentTeamId,
        role: currentTeam.role
      }
    }),
    [
      companyId,
      currentTeam.name,
      currentTeam.role,
      currentTeamId,
      session.company_name,
      session.identifier
    ]
  )
  return userContext
}

function LaunchDarklyClientLoader({ children }: { children: ReactNode }) {
  const ldClient = useLDClient()
  const userContext = useUserContext()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function identify(user) {
      try {
        await ldClient?.identify(user)
      } catch (e) {
        // swallow ld error
      }
    }

    if (isReady) {
      identify(userContext)
    }
  }, [
    isReady,
    userContext,
    ldClient,
    userContext.custom.teamId,
    userContext.email
  ])

  useEffect(() => {
    ldClient
      ?.waitUntilReady()
      .then(() => {
        setIsReady(true)
        // Set on window like the default client would so we can use this in other areas
        window.ldClient = ldClient
      })
      .catch(() => {}) // swallow ld error
  }, [ldClient])

  return isReady ? <>{children}</> : <BusyOverlay />
}

function getLDOptions() {
  return window.Cypress ? { streaming: false, sendEvents: false } : undefined
}

export function LaunchDarklyProvider({
  children,
  isEnabled
}: {
  children: ReactNode
  isEnabled?: boolean
}) {
  const userContext = useUserContext()
  const ldClientId = useStore(
    appConfig,
    (state) => state.LAUNCHDARKLY_CLIENT_SIDE_ID
  )

  if (!isEnabled) {
    return <>{children}</>
  }

  return (
    <LDProvider
      clientSideID={ldClientId}
      context={{ ...userContext, kind: 'user' }}
      deferInitialization={false}
      flags={DEFAULT_FEATURE_FLAG_VALUES}
      options={getLDOptions()}
    >
      <LaunchDarklyClientLoader>{children}</LaunchDarklyClientLoader>
    </LDProvider>
  )
}

// LDProvider never closes its client, so without this the anonymous client keeps streaming after login
function CloseLDClientOnUnmount({ children }: { children: ReactNode }) {
  const ldClient = useLDClient()

  useEffect(
    () => () => {
      ldClient?.close()
    },
    [ldClient]
  )

  return <>{children}</>
}

/**
 * For logged-out pages, where there's no user to identify. The SDK generates and caches a key
 * for the anonymous context. Flags are undefined until LD responds
 */
export function AnonymousLaunchDarklyProvider({
  children,
  flags
}: {
  children: ReactNode
  flags: Partial<typeof DEFAULT_FEATURE_FLAG_VALUES>
}) {
  const ldClientId = useStore(
    appConfig,
    (state) => state.LAUNCHDARKLY_CLIENT_SIDE_ID
  )

  if (isPrivateEdition()) return <>{children}</>

  return (
    <LDProvider
      clientSideID={ldClientId}
      context={{ kind: 'user', anonymous: true }}
      flags={flags}
      options={getLDOptions()}
    >
      <CloseLDClientOnUnmount>{children}</CloseLDClientOnUnmount>
    </LDProvider>
  )
}
